import { Autocomplete, TextField } from '@mui/material';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import useDebounce from '~/hooks/useDebounce';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from '~/assets/icons';
import styles from './BaseAutocomplete.module.scss';
import { cast } from '~/utils/helper';

const cx = classNames.bind(styles);

type AutocompleteProps = {
  options?: any[];
  onSearch: (...event: any[]) => void;
  onSelected: (...event: any[]) => void;
  onChangeText?: (text: string) => void;
  isOptionEqualToValue?: any | null;
  value?: any | null;

  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
};

const BaseAutocomplete = (props: AutocompleteProps) => {
  const { options = [], onSearch } = props;

  const { t } = useTranslation('admin/layout');

  const [searchInput, setSearchInput] = useState<string>('');
  const searchInputDebounce = useDebounce(searchInput);
  const [data, setData] = useState<any[]>(options);

  useEffect(() => {
    setData(options);
  }, [options]);

  useEffect(() => {
    if (typeof searchInputDebounce !== 'undefined') {
      if (searchInputDebounce.length) onSearch(searchInputDebounce);
      else setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputDebounce]);

  const isSelected: boolean = Object.values(props.value).length > 0;

  return (
    <Autocomplete
      options={data}
      getOptionLabel={(option) => cast(option?.username)}
      noOptionsText={t('noSearchResultsFound', { ns: 'admin/common' })}
      forcePopupIcon={false}
      filterOptions={(options) => options}
      value={props.value}
      isOptionEqualToValue={(option, value) => option.username === value.username}
      onInputChange={(event, newValue) => {
        setSearchInput(newValue);
        props.onChangeText?.(newValue);
      }}
      onChange={(event, newValue, reason) => {
        if (reason === 'clear') {
          props.onSelected(null);
        }
        if (newValue) props.onSelected(newValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          className={cx('input-search')}
          placeholder={t('searchUser', { ns: 'admin/common' })}
          sx={{
            '& .MuiInputBase-root': {
              color: isSelected ? '#123B6D' : 'inherit',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isSelected ? '#123B6D' : '#7C8796',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isSelected ? '#123B6D' : '#7C8796',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isSelected ? '#123B6D' : '#7C8796',
            },
            '& .MuiInputBase-input': {
              paddingLeft: '10px !important',
              fontSize: '14px',
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: <React.Fragment>{props.iconStart ?? <SearchIcon className='w-4 h-4' />}</React.Fragment>,
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            padding: '10px 12px',
            fontSize: '14px',
            color: '#161A1F',
          }}
        >
          {`${option.name} - ${option.username}`}
        </li>
      )}
    />
  );
};
export default memo(BaseAutocomplete);
